import { useParams } from 'react-router-dom';
import { usePlaceLinkingLabel } from '@/hooks/usePlaceLinkingLabel';
import { useProducerOfferings } from '@/hooks/useProducerOfferings';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { validateSlug } from '@/lib/slug';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { useCoverAsset } from '@/hooks/useCoverAsset';
import { useGalleryAssets } from '@/hooks/useGalleryAssets';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

import { captureAppError } from '@/lib/monitoring/sentry';
import { logSystemEvent, generateRequestId } from '@/lib/observability/logSystemEvent';
import { buildSystemEventContext } from '@/lib/observability/buildSystemEventContext';
import {
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  ImageIcon,
  MapPin,
  Sun,
  Pencil,
  ChevronDown,
  ShoppingBag,
  Store,
  Phone,
  Navigation,
  ExternalLink,
} from 'lucide-react';
import { useProducerById, useUpdateProducer } from '@/hooks/useMyProducers';
import { useProducerAccess } from '@/hooks/useProducerAccess';
import { useProducerCapabilities } from '@/hooks/useProducerCapabilities';
import { usePublishProducer, useUnpublishProducer } from '@/hooks/usePublishProducer';
import { usePublishedPlaces } from '@/hooks/useAdminPlaces';
import { useProducerFormData } from '@/hooks/useProducerFormData';
import {
  BasicInfoSection,
  ContactSection,
  ImagesSection,
  PublishingSection,
  FoodFikaSection,
  useProducerEditorState,
} from '@/features/producer-editor';
import { hasFoodFikaOffering, buildFoodFikaHint } from '@/lib/producer/foodFika';
import { UtensilsCrossed } from 'lucide-react';
import type { ProducerFormData } from '@/features/producer-editor/types';
import { PlaceSelector, SuggestPlaceDialog, PlaceSuggestionsList } from '@/components/producer';
import { GroupedOfferingSelector, buildGroupedOfferHint } from '@/components/producer/GroupedOfferingSelector';
import { useOfferingCatalog } from '@/hooks/useOfferingCatalog';
import { ShopCategorySelector } from '@/components/producer/ShopCategorySelector';
import { useDraftSessionId } from '@/hooks/useDraftSessionId';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { useTrackOnce } from '@/lib/analytics/useTrackOnce';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { Sparkles } from 'lucide-react';
import { ShareStudioSheet } from '@/features/share/ShareStudioSheet';
import { EntityShareAction } from '@/features/share/EntityShareAction';
import { mapProducerToShareTarget } from '@/features/share/mappers/mapProducerToShareTarget';
import { features } from '@/config/features';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { getEffectiveProducerLocation } from '@/lib/producer/getEffectiveLocation';
import { useGeocoding } from '@/features/producer-editor/hooks/useGeocoding';
import { EditorLayout, EditorSidebarSection, EditorSidebarCard, EditorSectionCard, EditorSectionItem, EditorActionCard } from '@/components/editor';
import { SaveButton } from '@/components/ui/SaveButton';
import { StickyBottomBar } from '@/components/ui/StickyBottomBar';
import { producerPublicPath } from '@/lib/paths';

// EditorSectionItem replaced by shared EditorSectionItem from @/components/editor

// ─── Preview component (left column) ───

function ProducerPreview({
  formData,
  coverUrlMobile,
  coverUrlDesktop,
  galleryUrls,
  isPublic,
  monogram,
  linkedPlace,
  offerings,
  offeringCatalogGroups,
}: {
  formData: ProducerFormData;
  coverUrlMobile: string | null | undefined;
  coverUrlDesktop: string | null | undefined;
  galleryUrls: string[];
  isPublic: boolean;
  monogram: string;
  linkedPlace: any;
  offerings: string[];
  offeringCatalogGroups: any[];
}) {
  return (
    <div className="space-y-6">
      {/* Hero / Cover */}
      <div className="rounded-xl overflow-hidden border border-border/40 bg-card">
        <AspectRatio ratio={16 / 7}>
          <div className="relative w-full h-full">
            {coverUrlMobile ? (
              <img
                src={coverUrlDesktop || coverUrlMobile}
                srcSet={coverUrlDesktop ? `${coverUrlMobile} 640w, ${coverUrlDesktop} 1280w` : undefined}
                sizes="(max-width: 768px) 640px, 1280px"
                alt={`Omslag för ${formData.name || 'gården'}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-muted via-muted/80 to-muted/60 flex items-center justify-center">
                <span className="text-5xl sm:text-6xl font-semibold text-muted-foreground/30 select-none">
                  {monogram}
                </span>
              </div>
            )}

            {/* Bottom gradient overlay with title */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-5 sm:p-6">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-semibold text-white tracking-tight">
                    {formData.name || 'Namnlös gård'}
                  </h2>
                  {formData.shortDescription && (
                    <p className="text-sm text-white/80 mt-1 line-clamp-2 max-w-lg">
                      {formData.shortDescription}
                    </p>
                  )}
                </div>
                <Badge
                  variant={isPublic ? 'default' : 'secondary'}
                  className="shadow-sm backdrop-blur-sm bg-background/80 text-foreground text-xs shrink-0"
                >
                  {isPublic ? 'Publicerad' : 'Utkast'}
                </Badge>
              </div>
            </div>
          </div>
        </AspectRatio>
      </div>

      {/* Gallery strip */}
      {galleryUrls.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {galleryUrls.slice(0, 6).map((url, i) => (
            <div key={i} className="shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-border/40">
              <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      )}

      {/* Content preview */}
      <div className="space-y-4 px-1">
        {/* Location */}
        {linkedPlace && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 shrink-0" />
            <span>
              {linkedPlace.name}
              {linkedPlace.municipality && `, ${linkedPlace.municipality}`}
            </span>
          </div>
        )}

        {/* Intro text preview */}
        {formData.longDescription ? (
          <div className="prose prose-sm max-w-none text-foreground/80 line-clamp-4" dangerouslySetInnerHTML={{ __html: formData.longDescription }} />
        ) : formData.shortDescription ? (
          <p className="text-sm text-foreground/80">{formData.shortDescription}</p>
        ) : (
          <p className="text-sm text-muted-foreground italic">Ingen beskrivning ännu. Skriv er berättelse i panelen till höger.</p>
        )}

        {/* Offering tags */}
        {offerings.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {offerings.slice(0, 8).map((key) => {
              const label = offeringCatalogGroups
                .flatMap((g: any) => g.types || [])
                .find((t: any) => t.key === key)?.label_sv || key;
              return (
                <Badge key={key} variant="outline" className="text-xs font-normal">
                  {label}
                </Badge>
              );
            })}
            {offerings.length > 8 && (
              <Badge variant="outline" className="text-xs font-normal">
                +{offerings.length - 8}
              </Badge>
            )}
          </div>
        )}

        {/* Opening hours */}
        {formData.openingHoursText && (
          <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg px-4 py-3 border border-border/30">
            <p className="font-medium text-foreground text-xs uppercase tracking-wide mb-1">Besöksöppet</p>
            <p className="whitespace-pre-line">{formData.openingHoursText}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main page component ───

export default function ProducerPlacePresentationPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, isLoading, error, refetch } = useProducerById(farmId);
  const updateProducerMutation = useUpdateProducer();
  const { hasCapability } = useProducerAccess(farmId !== 'demo' ? farmId : undefined);
  const { checklist, invalidate } = useProducerCapabilities(farmId);
  const publishMutation = usePublishProducer();
  const unpublishMutation = useUnpublishProducer();
  const draftSessionId = useDraftSessionId(`producer-media-${farmId ?? 'demo'}`);
  const [slugError, setSlugError] = useState<string | null>(null);

  const isDemo = farmId === 'demo';
  const canEdit = isDemo || hasCapability('canEditProducerInfo');

  const { initialFormData } = useProducerFormData(producer, isDemo, farmId);

  const [showLongDescription, setShowLongDescription] = useState(false);
  const [showShareStudio, setShowShareStudio] = useState(false);

  // ─── Offerings state ───
  const { offerings: offeringsFromDb, saveOfferings: saveOfferingsToDb } = useProducerOfferings(farmId !== 'demo' ? farmId : undefined);
  const { groups: offeringCatalogGroups } = useOfferingCatalog();
  const [offerings, setOfferings] = useState<string[]>([]);
  const [offeringsOther, setOfferingsOther] = useState('');
  const [shopCategories, setShopCategories] = useState<string[]>([]);
  const offeringsInitialized = useRef(false);
  const offeringsDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!offeringsInitialized.current && (offeringsFromDb.length > 0 || producer)) {
      setOfferings(offeringsFromDb);
      setOfferingsOther((producer as any)?.offering_note ?? '');
      offeringsInitialized.current = true;
    }
  }, [producer, offeringsFromDb]);

  const saveOfferingsNow = useCallback(async (
    nextOfferings: string[],
    nextOther: string,
    nextShopCategories: string[],
  ) => {
    if (!farmId || !producer || isDemo) return;
    if ((await import('@/lib/supportPreview')).isSupportPreviewActive()) return;
    await saveOfferingsToDb(nextOfferings);
    const { supabase } = await import('@/integrations/supabase/client');
    await supabase
      .from('producers')
      .update({ offering_note: nextOther.trim() || null })
      .eq('id', farmId);
  }, [farmId, producer, isDemo, saveOfferingsToDb]);

  const debounceSaveOfferings = useCallback((
    nextOfferings: string[],
    nextOther: string,
    nextShopCats: string[],
  ) => {
    if (offeringsDebounceRef.current) clearTimeout(offeringsDebounceRef.current);
    offeringsDebounceRef.current = setTimeout(
      () => saveOfferingsNow(nextOfferings, nextOther, nextShopCats),
      600,
    );
  }, [saveOfferingsNow]);

  const handleOfferingsChange = useCallback((next: string[]) => {
    setOfferings(next);
    debounceSaveOfferings(next, offeringsOther, shopCategories);
  }, [debounceSaveOfferings, offeringsOther, shopCategories]);

  const handleOtherTextChange = useCallback((text: string) => {
    setOfferingsOther(text);
    debounceSaveOfferings(offerings, text, shopCategories);
  }, [debounceSaveOfferings, offerings, shopCategories]);

  const handleShopCategoriesChange = useCallback((next: string[]) => {
    setShopCategories(next);
    debounceSaveOfferings(offerings, offeringsOther, next);
  }, [debounceSaveOfferings, offerings, offeringsOther]);

  // ─── Activity count ───
  const { data: activeActivityCount = 0 } = useQuery({
    queryKey: ['producer-active-activity-count', farmId],
    enabled: !!farmId && !isDemo,
    queryFn: async () => {
      const { count } = await supabase
        .from('producer_events')
        .select('id', { count: 'exact', head: true })
        .eq('producer_id', farmId!)
        .in('status', ['draft', 'public']);
      return count ?? 0;
    },
    staleTime: 60_000,
  });

  const offeringsProducerContext = useMemo(() => {
    if (!farmId || isDemo) return undefined;
    return {
      producerId: farmId,
      activeActivityCount,
      basePath: `/app/producer/farm/${farmId}`,
    };
  }, [farmId, isDemo, activeActivityCount]);

  const editorState = useProducerEditorState({
    initialData: initialFormData,
    onSave: async (data) => {
      if (isDemo) {
        toast.success('Sparat (demo)');
        return;
      }
      if (!producer) return;

      const payload: Record<string, any> = {
        name: data.name,
        short_description: data.shortDescription,
        description: data.longDescription || null,
        categories: data.categories,
        capability_suggestion: data.capabilitySuggestion || null,
        address_text: data.address || null,
        city: data.city || null,
        region: data.region || null,
        lat: data.lat ? Number(String(data.lat).replace(',', '.')) || null : null,
        lng: data.lng ? Number(String(data.lng).replace(',', '.')) || null : null,
        phone: data.phone,
        email: data.email,
        website: data.website,
        instagram: data.instagram,
        facebook: data.facebook,
        opening_hours_text: data.openingHoursText || null,
        phone_public: data.phonePublic,
        email_public: data.emailPublic,
        website_public: data.websitePublic,
        instagram_public: data.instagramPublic,
        facebook_public: data.facebookPublic,
        food_description: data.foodDescription || null,
        food_serving_types: data.foodServingTypes,
      };

      if (data.slug) {
        const slugErr = validateSlug(data.slug);
        if (slugErr) {
          setSlugError(slugErr);
          throw new Error(slugErr);
        }
      }
      if (data.slug !== (producer.slug || '')) {
        payload.slug = data.slug || null;
      }

      try {
        await updateProducerMutation.mutateAsync({
          id: producer.id,
          updates: payload,
        });
        setSlugError(null);
        toast.success('Ändringar sparade', { duration: 3000 });
      } catch (err: any) {
        const msg = err?.message || '';
        if (msg.includes('producers_slug_key') || msg.includes('duplicate key') || msg.includes('unique')) {
          setSlugError('Den här adressen används redan av en annan gård.');
          throw err;
        }
        throw err;
      }
    },
  });

  // Place linking
  const placeId = producer?.place_id ?? null;
  const { data: publishedPlaces } = usePublishedPlaces();
  const publishedLinkedPlace = publishedPlaces?.find((p) => p.id === placeId) ?? null;
  const { data: linkedPlaceLabel, isLoading: isLabelLoading } = usePlaceLinkingLabel(placeId);
  const linkedPlace = linkedPlaceLabel ?? publishedLinkedPlace ?? null;

  // Status
  const isPublic = producer?.status === 'public';
  const isSubmitting = publishMutation.isPending || unpublishMutation.isPending;

  // Analytics
  useTrackOnce('place_presentation_opened', {
    producer_id: farmId || '',
    place_id: placeId || null,
    is_public: isPublic,
  }, farmId);

  const handlePreviewClick = () => {
    if (producer?.slug) {
      track('place_presentation_preview_clicked', {
        producer_id: producer.id,
        place_id: placeId || null,
      });
      window.open(`/producer/${producer.slug}?preview=1`, '_blank');
    }
  };

  const handlePublishToggle = async (publish: boolean) => {
    if (isDemo || !producer) return;

    // If dirty, save first (canonical pattern: publish includes pending edits)
    if (editorState.isDirty) {
      const saved = await editorState.save();
      if (!saved) {
        toast.error('Kunde inte spara ändringar innan publicering.');
        return;
      }
    }

    track(publish ? 'place_presentation_publish_clicked' : 'place_presentation_unpublish_clicked', {
      producer_id: producer.id,
      place_id: placeId || null,
    });

    const requestId = generateRequestId();

    try {
      if (publish) {
        await publishMutation.mutateAsync(producer.id);
        toast.success('Publicerad! Er sida syns nu för besökare.', { duration: 4000 });
      } else {
        await unpublishMutation.mutateAsync(producer.id);
        toast.success('Avpublicerad. Sidan är inte längre synlig.', { duration: 4000 });
      }
      invalidate();
    } catch (err) {
      toast.error(
        publish ? 'Kunde inte publicera. Försök igen om en stund.' : 'Kunde inte avpublicera. Försök igen om en stund.',
        { duration: 5000 },
      );
      captureAppError(err, {
        feature: 'place_presentation',
        action: publish ? 'publish' : 'unpublish',
        producer_id: producer.id,
        request_id: requestId,
      });
      logSystemEvent({
        level: 'error',
        event_key: publish ? 'producer_publish_failed' : 'producer_unpublish_failed',
        entity_type: 'producer',
        entity_id: producer.id,
        producer_id: producer.id,
        message: `Place presentation ${publish ? 'publish' : 'unpublish'} failed: ${err instanceof Error ? err.message : 'unknown'}`,
        request_id: requestId,
        context: buildSystemEventContext({ feature: 'place_presentation', action: publish ? 'publish' : 'unpublish', surface: 'place_presentation' }),
      });
    }
  };

  // Cover asset from media_assets SoT
  const { url640: coverUrl640, url1280: coverUrl1280 } = useCoverAsset('producer', isDemo ? undefined : producer?.id);
  const coverUrlMobile = coverUrl640;
  const coverUrlDesktop = coverUrl1280 || coverUrl640;

  // Gallery assets
  const { urls320: galleryUrls320 } = useGalleryAssets('producer', isDemo ? undefined : producer?.id);

  // Producer share target
  const shareTarget = useMemo(() => {
    if (!producer) return null;
    return mapProducerToShareTarget({
      producerId: producer.id,
      producerName: producer.name,
      producerSlug: producer.slug ?? producer.id,
      heroImageUrl: coverUrl1280 ?? coverUrl640 ?? null,
      tagline: producer.short_description ?? null,
      placeName: linkedPlace?.name ?? null,
    });
  }, [producer, coverUrl1280, coverUrl640, linkedPlace]);

  const producerPublicUrl = useMemo(
    () => (producer?.slug ? `${window.location.origin}${producerPublicPath(producer.slug)}` : null),
    [producer?.slug],
  );

  const monogram = (editorState.formData.name || 'F')
    .split(/\s+/)
    .map(w => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Loading/error states
  if (isLoading && !isDemo) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Laddar..." title="Sida">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  if (error && !isDemo) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Fel" title="Sida">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Kunde inte ladda gården.</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => refetch()} className="mt-4">
          Försök igen
        </Button>
      </ProducerLayout>
    );
  }

  // Hints for missing items
  const hints: string[] = [];
  if (!checklist?.hasCoverImage) hints.push('Omslagsbild saknas');
  if (!checklist?.hasNameAndDescription) hints.push('Namn eller beskrivning saknas');
  if (!checklist?.hasLinkedPlace) hints.push('Ingen kopplad plats');

  // ─── Sidebar content ───
  const sidebarContent = (
    <div className="space-y-5">
      <EditorActionCard
        isPublished={isPublic}
        onPublish={() => handlePublishToggle(true)}
        onUnpublish={() => handlePublishToggle(false)}
        publishDisabled={isSubmitting}
        publishSlot={
          isPublic ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" disabled={isSubmitting} className="text-xs">
                  <EyeOff className="h-3.5 w-3.5 mr-1" />
                  Avpublicera
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Avpublicera er sida?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Er sida döljs för besökare. Ni kan publicera igen när som helst.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Avbryt</AlertDialogCancel>
                  <AlertDialogAction onClick={() => handlePublishToggle(false)}>
                    Avpublicera
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" disabled={isSubmitting || !checklist?.allMet} className="text-xs">
                  <Eye className="h-3.5 w-3.5 mr-1" />
                  Publicera
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Publicera er sida?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Er sida blir synlig för alla besökare på Farmly.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Avbryt</AlertDialogCancel>
                  <AlertDialogAction onClick={() => handlePublishToggle(true)}>
                    Publicera
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )
        }
        onSave={editorState.save}
        isSaving={editorState.isSaving}
        saveDisabled={!editorState.isDirty || updateProducerMutation.isPending}
        onView={producer?.slug ? handlePreviewClick : undefined}
        secondaryAction={
          features.enableShareStudio && isPublic ? (
            <EntityShareAction
              entityType="producer"
              publicUrl={producerPublicUrl}
              buttonLabel="Dela"
              buttonClassName="w-full gap-1.5 text-xs"
              socialAction={shareTarget ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => setShowShareStudio(true)}
                >
                  <Sparkles className="h-4 w-4" />
                  Share Studio
                </Button>
              ) : undefined}
            />
          ) : undefined
        }
        footer={
          hints.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {hints.map((hint) => (
                <span key={hint} className="text-[11px] text-muted-foreground bg-muted px-2 py-0.5 rounded-md">
                  {hint}
                </span>
              ))}
            </div>
          ) : undefined
        }
      />

      {/* ─── Content sections ─── */}
      <EditorSidebarSection heading="Innehåll">
        <EditorSectionCard>
          <EditorSectionItem
            title="Er berättelse"
            icon={Pencil}
            defaultOpen={false}
            hint={editorState.formData.shortDescription || 'Berätta om er'}
          >
            <BasicInfoSection
              formData={editorState.formData}
              errors={editorState.errors}
              updateField={editorState.updateField}
              showLongDescription={showLongDescription}
              onToggleLongDescription={setShowLongDescription}
              producerId={isDemo ? undefined : producer?.id}
              currentSlug={producer?.slug ?? null}
              slugError={slugError}
              onSlugErrorClear={() => setSlugError(null)}
            />
          </EditorSectionItem>

          <EditorSectionItem
            title="Kontakt"
            icon={Phone}
            defaultOpen={false}
            hint={producer?.phone?.trim() || producer?.email?.trim() ? 'Kontaktuppgifter tillagda' : 'Lägg till kontaktuppgifter'}
          >
            <ContactSection
              formData={editorState.formData}
              updateField={editorState.updateField}
            />
          </EditorSectionItem>
        </EditorSectionCard>
      </EditorSidebarSection>

      {/* ─── Images ─── */}
      <EditorSidebarSection heading="Bilder & känsla">
        <EditorSidebarCard>
          <EditorSectionItem
            title="Bilder"
            icon={ImageIcon}
            defaultOpen={false}
            hint={checklist?.hasCoverImage ? 'Omslagsbild tillagd' : 'Lägg till bilder'}
          >
            <ImagesSection
              producerId={isDemo ? undefined : producer?.id}
              draftSessionId={draftSessionId}
            />
          </EditorSectionItem>
        </EditorSidebarCard>
      </EditorSidebarSection>

      {/* ─── Offerings ─── */}
      <EditorSidebarSection heading="Erbjudanden">
        <EditorSectionCard>
          <EditorSectionItem
            title="Vad erbjuder ni här?"
            icon={ShoppingBag}
            defaultOpen={false}
            hint={buildGroupedOfferHint(offerings, offeringCatalogGroups)}
          >
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Välj det som bäst beskriver vad ni erbjuder besökare.
              </p>
              <GroupedOfferingSelector
                offerings={offerings}
                onOfferingsChange={handleOfferingsChange}
                otherText={offeringsOther}
                onOtherTextChange={handleOtherTextChange}
                producerContext={offeringsProducerContext}
              />
            </div>
          </EditorSectionItem>

          {offerings.includes('farm_shop') && farmId && !isDemo && (
            <EditorSectionItem
              title="Gårdsbutik"
              icon={Store}
              defaultOpen={false}
              hint={shopCategories.length > 0 ? `${shopCategories.length} kategorier valda` : 'Ange vad ni säljer'}
            >
              <ShopCategorySelector
                shopCategories={shopCategories}
                onShopCategoriesChange={handleShopCategoriesChange}
                shopEnabled={producer?.shop_enabled ?? false}
                producerId={farmId}
                basePath={`/app/producer/farm/${farmId}`}
              />
            </EditorSectionItem>
          )}
        </EditorSectionCard>
      </EditorSidebarSection>

      {/* ─── Mat & fika (offering-gated) ─── */}
      {hasFoodFikaOffering(offerings) && farmId && !isDemo && (
        <EditorSidebarSection heading="Mat & fika">
          <EditorSectionCard>
            <EditorSectionItem
              title="Mat & fika"
              icon={UtensilsCrossed}
              defaultOpen={false}
              hint={buildFoodFikaHint(
                (producer as any)?.food_description,
                (producer as any)?.food_serving_types ?? [],
              )}
            >
              <FoodFikaSection
                description={editorState.formData.foodDescription}
                servingTypes={editorState.formData.foodServingTypes}
                onDescriptionChange={(html) => editorState.updateField('foodDescription', html)}
                onServingTypesChange={(types) => editorState.updateField('foodServingTypes', types)}
              />
            </EditorSectionItem>
          </EditorSectionCard>
        </EditorSidebarSection>
      )}

      {/* ─── Location ─── */}
      <EditorSidebarSection heading="Plats & karta">
        <EditorSectionCard>
          <EditorSectionItem
            title="Syns på kartan"
            icon={MapPin}
            defaultOpen={false}
            hint={linkedPlace ? `Kopplad till ${linkedPlace.name}` : (placeId && isLabelLoading ? 'Kopplad…' : placeId ? 'Kopplad' : 'Inte kopplad än')}
          >
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Koppla din sida till en plats för att visas i karta och sök.
              </p>
              {!isDemo && producer && (
                <>
                  <div className="space-y-2">
                    <Label>Välj plats</Label>
                    <PlaceSelector
                      producerId={producer.id}
                      currentPlaceId={placeId}
                      disabled={!canEdit}
                    />
                  </div>

                  {linkedPlace && (
                    <div className="flex items-center gap-2 bg-primary/10 text-primary px-3 py-2 rounded-md text-sm">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      <span className="text-xs">
                        <strong>Kopplad:</strong> {linkedPlace.name}
                        {linkedPlace.municipality && `, ${linkedPlace.municipality}`}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Hittar du inte rätt plats?</span>
                    <SuggestPlaceDialog
                      producerId={producer.id}
                      trigger={
                        <Button variant="link" className="h-auto p-0 text-xs" disabled={!canEdit}>
                          Föreslå ny plats
                        </Button>
                      }
                      disabled={!canEdit}
                    />
                  </div>

                  <PlaceSuggestionsList producerId={producer.id} />
                </>
              )}
            </div>
          </EditorSectionItem>

          <AddressAccordionContent
            producer={producer}
            linkedPlace={linkedPlace}
            canEdit={canEdit}
            isDemo={isDemo}
            editorState={editorState}
            isSaving={updateProducerMutation.isPending}
          />
        </EditorSectionCard>
      </EditorSidebarSection>

      {/* ─── Seasons (placeholder) ─── */}
      <EditorSidebarSection heading="Övrigt">
        <EditorSidebarCard>
          <EditorSectionItem
            title="Säsonger"
            icon={Sun}
            defaultOpen={false}
            hint="Kommer snart"
          >
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Säsongsöverlagringar låter er anpassa hur platsen visas under olika årstider.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {(['Vår', 'Sommar', 'Höst', 'Vinter'] as const).map((season) => (
                  <div
                    key={season}
                    className="flex items-center gap-2 p-2 rounded-lg border border-border bg-card text-xs"
                  >
                    <Sun className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-foreground">{season}</span>
                    <Badge variant="outline" className="ml-auto text-[9px]">
                      Snart
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </EditorSectionItem>
        </EditorSidebarCard>
      </EditorSidebarSection>
    </div>
  );

  return (
    <ProducerLayout
      farmId={farmId || 'demo'}
      producerName={editorState.formData.name || 'Din gård'}
      title="Sida"
    >
      <EditorLayout
        main={
          <ProducerPreview
            formData={editorState.formData}
            coverUrlMobile={coverUrlMobile}
            coverUrlDesktop={coverUrlDesktop}
            galleryUrls={galleryUrls320}
            isPublic={isPublic}
            monogram={monogram}
            linkedPlace={linkedPlace}
            offerings={offerings}
            offeringCatalogGroups={offeringCatalogGroups}
          />
        }
        sidebar={sidebarContent}
      />

      {/* Mobile sticky action bar */}
      <StickyBottomBar>
        <SaveButton
          isSaving={editorState.isSaving}
          disabled={!editorState.isDirty || updateProducerMutation.isPending}
          onClick={editorState.save}
        />
      </StickyBottomBar>

      {/* Producer Share Studio */}
      {shareTarget && (
        <ShareStudioSheet
          open={showShareStudio}
          onOpenChange={setShowShareStudio}
          target={shareTarget}
        />
      )}
    </ProducerLayout>
  );
}

// ─── Address section content (inside sidebar accordion) ───

function AddressAccordionContent({
  producer,
  linkedPlace,
  canEdit,
  isDemo,
  editorState,
  isSaving,
}: {
  producer: ReturnType<typeof useProducerById>['producer'];
  linkedPlace: any;
  canEdit: boolean;
  isDemo: boolean;
  editorState: { formData: ProducerFormData; updateField: <K extends keyof ProducerFormData>(key: K, value: ProducerFormData[K]) => void; isDirty: boolean; isSaving: boolean; save: () => Promise<any> };
  isSaving: boolean;
}) {
  const updateProducer = useUpdateProducer();
  const { geocode, isGeocoding, geocodeStatus } = useGeocoding();

  const [overrideEnabled, setOverrideEnabled] = useState(producer?.address_override_enabled ?? false);
  const [overrideAddress, setOverrideAddress] = useState(producer?.address_override_text || '');
  const [overrideMunicipality, setOverrideMunicipality] = useState(producer?.municipality_override || '');
  const [overrideRegion, setOverrideRegion] = useState(producer?.region_override || '');
  const [overrideLatOverride, setOverrideLatOverride] = useState<string>(producer?.lat_override != null ? String(producer.lat_override) : '');
  const [overrideLngOverride, setOverrideLngOverride] = useState<string>(producer?.lng_override != null ? String(producer.lng_override) : '');
  const [overrideHasChanges, setOverrideHasChanges] = useState(false);

  useEffect(() => {
    if (producer) {
      setOverrideEnabled(producer.address_override_enabled ?? false);
      setOverrideAddress(producer.address_override_text || '');
      setOverrideMunicipality(producer.municipality_override || '');
      setOverrideRegion(producer.region_override || '');
      setOverrideLatOverride(producer.lat_override != null ? String(producer.lat_override) : '');
      setOverrideLngOverride(producer.lng_override != null ? String(producer.lng_override) : '');
      setOverrideHasChanges(false);
    }
  }, [producer?.id, producer?.address_override_enabled]);

  const handleOverrideToggle = (checked: boolean) => {
    setOverrideEnabled(checked);
    setOverrideHasChanges(true);
  };

  const handleGeocodePrimary = async () => {
    const result = await geocode({
      address: editorState.formData.address,
      city: editorState.formData.city,
      region: editorState.formData.region,
    });
    if (result) {
      editorState.updateField('lat', String(result.lat));
      editorState.updateField('lng', String(result.lng));
    }
  };

  const handleGeocodeOverride = async () => {
    const result = await geocode({
      address: overrideAddress,
      city: overrideMunicipality,
      region: overrideRegion,
    });
    if (result) {
      setOverrideLatOverride(String(result.lat));
      setOverrideLngOverride(String(result.lng));
      setOverrideHasChanges(true);
    }
  };

  const handleOverrideSave = async () => {
    if (!producer || isDemo) return;

    const updates = overrideEnabled
      ? {
          address_override_enabled: true,
          address_override_text: overrideAddress.trim() || null,
          municipality_override: overrideMunicipality.trim() || null,
          region_override: overrideRegion.trim() || null,
          lat_override: overrideLatOverride ? Number(overrideLatOverride.replace(',', '.')) || null : null,
          lng_override: overrideLngOverride ? Number(overrideLngOverride.replace(',', '.')) || null : null,
        }
      : {
          address_override_enabled: false,
          address_override_text: null,
          municipality_override: null,
          region_override: null,
          lat_override: null,
          lng_override: null,
        };

    try {
      await updateProducer.mutateAsync({ id: producer.id, updates });
      setOverrideHasChanges(false);
      toast.success('Alternativ mötesplats sparad');
    } catch {
      toast.error('Kunde inte spara');
    }
  };

  const { formData, updateField } = editorState;
  const addressHint = formData.address || formData.city || (linkedPlace ? 'Från kopplad plats' : 'Ingen adress');

  return (
    <EditorSectionItem
      title="Hitta hit"
      icon={Navigation}
      defaultOpen={false}
      hint={addressHint}
    >
      <div className="space-y-5">
        {/* Primary address */}
        <div>
          <h4 className="text-xs font-medium text-foreground mb-2">Besöksadress</h4>
          <div className="space-y-3">
            <div>
              <Label htmlFor="producer-address" className="text-xs">Adress</Label>
              <Input
                id="producer-address"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                placeholder="t.ex. Gårdsvägen 1"
                disabled={!canEdit}
                className="text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="producer-city" className="text-xs">Ort</Label>
                <Input
                  id="producer-city"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  placeholder="Gnesta"
                  disabled={!canEdit}
                  className="text-sm"
                />
              </div>
              <div>
                <Label htmlFor="producer-region" className="text-xs">Region</Label>
                <Input
                  id="producer-region"
                  value={formData.region}
                  onChange={(e) => updateField('region', e.target.value)}
                  placeholder="Södermanland"
                  disabled={!canEdit}
                  className="text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="producer-lat" className="text-xs">Lat</Label>
                <Input
                  id="producer-lat"
                  type="number"
                  step="any"
                  value={formData.lat}
                  onChange={(e) => updateField('lat', e.target.value)}
                  placeholder="59.05"
                  disabled={!canEdit}
                  className="font-mono text-xs"
                />
              </div>
              <div>
                <Label htmlFor="producer-lng" className="text-xs">Lng</Label>
                <Input
                  id="producer-lng"
                  type="number"
                  step="any"
                  value={formData.lng}
                  onChange={(e) => updateField('lng', e.target.value)}
                  placeholder="17.31"
                  disabled={!canEdit}
                  className="font-mono text-xs"
                />
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGeocodePrimary}
              disabled={!canEdit || isGeocoding || (!formData.address && !formData.city)}
              className="text-xs"
            >
              {isGeocoding ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Hämtar…
                </>
              ) : (
                <>
                  <MapPin className="mr-1.5 h-3.5 w-3.5" />
                  Hämta koordinater
                </>
              )}
            </Button>
            {geocodeStatus === 'success' && (
              <p className="text-xs text-green-700 dark:text-green-400">Koordinater uppdaterade.</p>
            )}
            {(geocodeStatus === 'not_found' || geocodeStatus === 'invalid') && (
              <p className="text-xs text-amber-700 dark:text-amber-400">Kunde inte hämta koordinater. Kontrollera adressen.</p>
            )}
          </div>
        </div>

        {/* Linked place context */}
        {linkedPlace && (
          <div className="border-t border-border pt-3">
            <p className="text-xs text-muted-foreground">
              Kopplad plats: <strong>{linkedPlace.name || '–'}</strong>
              {linkedPlace.municipality && `, ${linkedPlace.municipality}`}
            </p>
          </div>
        )}

        {/* Override */}
        <div className="border-t border-border pt-3">
          <div className="flex items-start gap-2 p-3 rounded-lg border border-border bg-card">
            <Switch
              id="address-override-sida"
              checked={overrideEnabled}
              onCheckedChange={handleOverrideToggle}
              disabled={!canEdit}
              className="scale-90 mt-0.5"
            />
            <div className="space-y-0.5">
              <Label htmlFor="address-override-sida" className="text-xs font-medium cursor-pointer">
                Annan mötesplats
              </Label>
              <p className="text-[11px] text-muted-foreground leading-snug">
                Om besökare ska till en annan plats.
              </p>
            </div>
          </div>

          {overrideEnabled && (
            <div className="space-y-3 mt-3">
              <div>
                <Label htmlFor="sida-override-address" className="text-xs">Alternativ adress</Label>
                <Input
                  id="sida-override-address"
                  value={overrideAddress}
                  onChange={(e) => { setOverrideAddress(e.target.value); setOverrideHasChanges(true); }}
                  placeholder="t.ex. Parkeringen vid sjön"
                  disabled={!canEdit}
                  className="text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="sida-override-municipality" className="text-xs">Ort</Label>
                  <Input
                    id="sida-override-municipality"
                    value={overrideMunicipality}
                    onChange={(e) => { setOverrideMunicipality(e.target.value); setOverrideHasChanges(true); }}
                    placeholder="Sigtuna"
                    disabled={!canEdit}
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="sida-override-region" className="text-xs">Region</Label>
                  <Input
                    id="sida-override-region"
                    value={overrideRegion}
                    onChange={(e) => { setOverrideRegion(e.target.value); setOverrideHasChanges(true); }}
                    placeholder="Uppland"
                    disabled={!canEdit}
                    className="text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="sida-override-lat" className="text-xs">Lat</Label>
                  <Input
                    id="sida-override-lat"
                    type="number"
                    step="any"
                    value={overrideLatOverride}
                    onChange={(e) => { setOverrideLatOverride(e.target.value); setOverrideHasChanges(true); }}
                    placeholder="59.05"
                    disabled={!canEdit}
                    className="font-mono text-xs"
                  />
                </div>
                <div>
                  <Label htmlFor="sida-override-lng" className="text-xs">Lng</Label>
                  <Input
                    id="sida-override-lng"
                    type="number"
                    step="any"
                    value={overrideLngOverride}
                    onChange={(e) => { setOverrideLngOverride(e.target.value); setOverrideHasChanges(true); }}
                    placeholder="17.31"
                    disabled={!canEdit}
                    className="font-mono text-xs"
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGeocodeOverride}
                disabled={!canEdit || isGeocoding || (!overrideAddress && !overrideMunicipality)}
                className="text-xs"
              >
                {isGeocoding ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Hämtar…
                  </>
                ) : (
                  <>
                    <MapPin className="mr-1.5 h-3.5 w-3.5" />
                    Hämta koordinater
                  </>
                )}
              </Button>
            </div>
          )}

          {overrideHasChanges && canEdit && (
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleOverrideSave}
                disabled={updateProducer.isPending}
                size="sm"
                variant="outline"
                className="text-xs"
              >
                {updateProducer.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Sparar...
                  </>
                ) : overrideEnabled ? (
                  'Spara mötesplats'
                ) : (
                  'Spara'
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </EditorSectionItem>
  );
}
